import {
  BookedSkatingPass,
  UserAccount,
  UserAccountProfileResponse,
  UserAccountProfileWaiver,
} from '@types';
import { PDFViewerModal } from '_components/atoms';
import AdminService from '_services/AdminService';
import HelperMethods from '_utils/HelperMethods';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const isMaxUsesExhausted = (pass: BookedSkatingPass): boolean => {
  const maxUses = HelperMethods.getEffectiveMaxUses(pass);
  return maxUses != null && Number(pass.redeemedCount || 0) >= maxUses;
};

const getPassStatusLabel = (pass: BookedSkatingPass): string => {
  if (!pass.isActive) return 'Inactive';
  if (isMaxUsesExhausted(pass)) return 'Exhausted';
  return 'Active';
};

const getPassStatusClasses = (pass: BookedSkatingPass): string => {
  if (!pass.isActive) return 'bg-gray-200 text-gray-700';
  if (isMaxUsesExhausted(pass)) return 'bg-amber-100 text-amber-800';
  return 'bg-emerald-100 text-emerald-800';
};

const getPhone = (user: UserAccount): string =>
  user.phone || user.phoneNumber || '';

const UserAccountDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState<UserAccount | null>(null);
  const [passes, setPasses] = useState<BookedSkatingPass[]>([]);
  const [waivers, setWaivers] = useState<UserAccountProfileWaiver[]>([]);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) {
        setError('User not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = (await AdminService.getUserAccountProfile(
          Number(userId),
        )) as unknown as UserAccountProfileResponse;

        setUser(response.user);
        setPasses(response.passes || []);
        setWaivers(response.waivers || []);
      } catch (err) {
        console.error('Error fetching user account profile:', err);
        setError('Unable to load this customer account.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({ isOpen: true, url, title });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({ isOpen: false, url: '', title: '' });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          Loading customer account...
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <Link
          to="/admin/user-accounts"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
        >
          ← Back to Customers
        </Link>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          {error || 'User not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/admin/user-accounts"
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mb-2"
          >
            ← Back to Customers
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {user.givenName} {user.familyName}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            View passes and waivers linked to this account
          </p>
        </div>
      </div>

      {/* Profile header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-sm">
              {`${(user.givenName || '?')[0] || ''}${(user.familyName || '?')[0] || ''}`}
            </div>
            <div className="ml-4">
              <div className="text-lg font-semibold text-gray-900">
                {user.givenName} {user.familyName}
              </div>
              <div className="text-sm text-gray-900">{user.email}</div>
              {getPhone(user) && (
                <div className="text-sm text-gray-500">{getPhone(user)}</div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                (user.roleKey || '').toLowerCase() === 'moderator'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
              }`}
            >
              {(user.roleKey || '').toUpperCase()}
            </span>
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                user.isActive
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {user.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Passes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Season Passes &amp; Ticket Passes
          </h2>
          <span className="text-sm text-gray-600">
            {passes.length} pass{passes.length === 1 ? '' : 'es'}
          </span>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pass
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {passes.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-10 text-center text-gray-500"
                  >
                    No passes linked to this account
                  </td>
                </tr>
              ) : (
                passes.map(pass => (
                  <tr key={pass.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {pass.passName || pass.passType || 'Pass'}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {pass.passType}
                        {pass.selectedPassType
                          ? ` · ${pass.selectedPassType}`
                          : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-blue-600">
                        {pass.barcodeId}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getPassStatusClasses(pass)}`}
                      >
                        {getPassStatusLabel(pass)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pass.maxUses === null
                          ? `${pass.redeemedCount || 0} / Unlimited`
                          : `${pass.redeemedCount || 0} / ${pass.maxUses}`}
                      </div>
                      {pass.maxUses !== null && (
                        <div className="text-xs text-gray-500">
                          {Math.max(
                            0,
                            Number(pass.maxUses) -
                              Number(pass.redeemedCount || 0),
                          )}{' '}
                          remaining
                        </div>
                      )}
                      {pass.maxUses !== null && (
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1 max-w-[140px]">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${Math.min(
                                100,
                                (Number(pass.redeemedCount || 0) /
                                  Number(pass.maxUses)) *
                                  100,
                              )}%`,
                            }}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-200">
          {passes.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No passes linked to this account
            </div>
          ) : (
            passes.map(pass => (
              <div key={pass.id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {pass.passName || pass.passType || 'Pass'}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {pass.passType}
                      {pass.selectedPassType
                        ? ` · ${pass.selectedPassType}`
                        : ''}
                    </div>
                  </div>
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getPassStatusClasses(pass)}`}
                  >
                    {getPassStatusLabel(pass)}
                  </span>
                </div>
                <div className="text-sm text-blue-600 font-semibold">
                  {pass.barcodeId}
                </div>
                <div className="text-sm text-gray-700">
                  {pass.maxUses === null
                    ? `${pass.redeemedCount || 0} / Unlimited uses`
                    : `${pass.redeemedCount || 0} / ${pass.maxUses} uses (${Math.max(0, Number(pass.maxUses) - Number(pass.redeemedCount || 0))} remaining)`}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Waivers */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Liability Waivers
          </h2>
          <span className="text-sm text-gray-600">
            {waivers.length} waiver{waivers.length === 1 ? '' : 's'}
          </span>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Skater
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completed
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {waivers.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-gray-500"
                  >
                    No waivers linked to this account
                  </td>
                </tr>
              ) : (
                waivers.map(waiver => {
                  const status =
                    waiver.status ||
                    (waiver.accepted && waiver.pdfFilePath
                      ? 'Completed'
                      : 'Pending');
                  const source = waiver.source || 'Online';
                  return (
                    <tr
                      key={`${waiver.id}-${waiver.barcodeId}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {waiver.skaterName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {waiver.barcodeId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {source}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                            status === 'Completed'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {HelperMethods.formatDateTimeLocal(waiver.submittedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {waiver.pdfFilePath ? (
                          <button
                            onClick={() =>
                              openPdfViewer(
                                waiver.pdfFilePath!,
                                `Waiver - ${waiver.skaterName}`,
                              )
                            }
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            View Waiver
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">No PDF</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-200">
          {waivers.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No waivers linked to this account
            </div>
          ) : (
            waivers.map(waiver => {
              const status =
                waiver.status ||
                (waiver.accepted && waiver.pdfFilePath
                  ? 'Completed'
                  : 'Pending');
              const source = waiver.source || 'Online';
              return (
                <div
                  key={`${waiver.id}-${waiver.barcodeId}`}
                  className="p-4 space-y-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {waiver.skaterName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {waiver.barcodeId}
                      </div>
                    </div>
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                        status === 'Completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {source}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {HelperMethods.formatDateTimeLocal(waiver.submittedAt)}
                  </div>
                  {waiver.pdfFilePath ? (
                    <button
                      onClick={() =>
                        openPdfViewer(
                          waiver.pdfFilePath!,
                          `Waiver - ${waiver.skaterName}`,
                        )
                      }
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      View Waiver
                    </button>
                  ) : (
                    <span className="text-gray-400 text-sm">No PDF</span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />
    </div>
  );
};

export default UserAccountDetail;
